// src/pages/admin/MembersPane.js
import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const MEMBER_TYPE_LABEL = { REGULAR: "Regular", NOMINAL: "Nominal" };

function MembersPane() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [talukaFilter, setTalukaFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_desc");
  const [search, setSearch] = useState("");

  const [editingMember, setEditingMember] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // ✅ Full screen overlay (default ON)
  const [fullScreen, setFullScreen] = useState(true);

  /* =========================
     ✅ PAYMENT NORMALIZER
     - fixes: paid people showing pending
  ========================= */
  const normalizePayment = (data) => {
    const raw =
      data.payment_status ??
      data.paymentStatus ??
      data.paymentStatusText ??
      data.fee_status ??
      data.feeStatus ??
      data.isPaid ??
      data.paid ??
      data.paymentDone ??
      data.payment?.status ??
      data.payment?.state ??
      "";

    const s = String(raw || "").trim().toUpperCase();

    if (["PAID", "SUCCESS", "COMPLETED", "CAPTURED", "DONE", "RECEIVED"].includes(s)) return "PAID";
    if (raw === true) return "PAID";

    const amt =
      Number(
        data.paid_amount ??
          data.paidAmount ??
          data.amountPaid ??
          data.payment_amount ??
          data.paymentAmount ??
          0
      ) || 0;
    if (amt > 0) return "PAID";

    return "PENDING";
  };

  // ------------ LOAD FROM ALL SOURCES --------------
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const sources = ["memberRegister", "Members", "members"];
        const all = [];

        for (const colName of sources) {
          try {
            const snap = await getDocs(query(collection(db, colName)));
            if (snap.empty) continue;

            snap.forEach((d) => {
              const data = d.data() || {};

              // normalize created_at
              const created_at = data.created_at || data.createdAt || null;

              // normalize membership_type
              let mType = data.membership_type;
              if (!mType) {
                const memberId = String(data.member_id || "").trim();
                if (memberId.startsWith("SAAM-")) mType = "REGULAR";
                else mType = "NOMINAL";
              }

              const payNorm = normalizePayment(data);

              all.push({
                id: d.id,
                _source: colName,
                ...data,
                membership_type: mType,
                created_at,

                // ✅ normalize payment fields (non-breaking)
                payment_status: data.payment_status || payNorm,
                isPaid: payNorm === "PAID" || data.isPaid === true,
              });
            });
          } catch (e) {
            console.warn("[MembersPane] skip source", colName, e.message);
          }
        }

        // sort by created_at desc initially
        all.sort((a, b) => {
          const aa = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bb = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bb - aa;
        });

        setMembers(all);
      } catch (err) {
        console.error("Members load error:", err);
        setError(err.message || "Failed to load members.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* =========================
     ✅ MEMBERSHIP SERIES
     - NOMINAL: SAAM/NOMINAL/MEMBER/0675  (NO CHANGE)
     - REGULAR: SAAM-YYYY-0001
  ========================= */

  // ✅ NOMINAL ID generator (KEEP SAME FORMAT): SAAM/NOMINAL/MEMBER/0675
  const generateNominalId = async () => {
    // One-time: set membership_counters/nominal_member.lastNumber = your current last (e.g., 675)
    const ref = doc(db, "membership_counters", "nominal_member");

    const nextNo = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const current = snap.exists() ? Number(snap.data().lastNumber || 0) : 0;
      const next = current + 1;
      tx.set(ref, { lastNumber: next, updated_at: new Date().toISOString() }, { merge: true });
      return next;
    });

    return `SAAM/NOMINAL/MEMBER/${String(nextNo).padStart(4, "0")}`;
  };

  // ✅ REGULAR ID generator: SAAM-YYYY-0001 (approve-date year)
  const generateRegularId = async () => {
    const year = new Date().getFullYear();
    const ref = doc(db, "membership_counters", `regular_${year}`);

    const nextNo = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const current = snap.exists() ? Number(snap.data().lastNumber || 0) : 0;
      const next = current + 1;
      tx.set(ref, { year, lastNumber: next, updated_at: new Date().toISOString() }, { merge: true });
      return next;
    });

    return `SAAM-${year}-${String(nextNo).padStart(4, "0")}`;
  };

  /* =========================
     ✅ MANUAL PAYMENT MARK (Proof-based)
     - Admin can mark PAID even if field missing
  ========================= */
  const markPaidManual = async (m, { utr = "" } = {}) => {
    const col = m._source || "members";
    const ref = doc(db, col, m.id);

    await updateDoc(ref, {
      payment_status: "PAID",
      payment_marked_by_admin: true,
      payment_marked_at: new Date().toISOString(),
      payment_manual_utr: utr || "",
      updated_at: serverTimestamp(),
    });

    setMembers((prev) =>
      prev.map((x) =>
        x.id === m.id && x._source === col
          ? {
              ...x,
              payment_status: "PAID",
              isPaid: true,
              payment_marked_by_admin: true,
              payment_manual_utr: utr || "",
            }
          : x
      )
    );
  };

  const handleApprove = async (m) => {
    try {
      const col = m._source || "members";
      const ref = doc(db, col, m.id);

      const paidNow =
        m.isPaid === true ||
        String(m.payment_status || "").toUpperCase() === "PAID" ||
        String(m.payment_status || "").toUpperCase() === "SUCCESS";

      // ✅ If not marked paid, allow admin to mark PAID based on proof
      if (!paidNow) {
        const ok = window.confirm(
          "Payment is not marked as PAID in system.\n\nIf you have payment proof, click OK to mark PAID and continue approval."
        );
        if (!ok) return;

        const utr = window.prompt("Enter UTR / Txn ID (optional):", "") || "";
        await markPaidManual(m, { utr });
      }

      const isNominal = String(m.membership_type || "").toUpperCase() === "NOMINAL";

      // ✅ keep existing ID if already assigned and not APPLIED
      let finalId = m.member_id && m.member_id !== "APPLIED" ? m.member_id : "";

      // ✅ generate only if missing
      if (!finalId) {
        finalId = isNominal ? await generateNominalId() : await generateRegularId();
      }

      await updateDoc(ref, {
        status: "approved",
        member_id: finalId,
        approved_at: new Date().toISOString(),
        member_portal_access: true,
        benefits_enabled: true,

        // ✅ ensure payment status stored cleanly too
        payment_status: "PAID",
        updated_at: serverTimestamp(),
      });

      setMembers((prev) =>
        prev.map((x) =>
          x.id === m.id && x._source === col
            ? {
                ...x,
                status: "approved",
                member_id: finalId,
                member_portal_access: true,
                benefits_enabled: true,
                payment_status: "PAID",
                isPaid: true,
              }
            : x
        )
      );
    } catch (err) {
      console.error("Approve error:", err);
      alert("Error approving member: " + err.message);
    }
  };

  const handleReject = async (m) => {
    try {
      const reason = window.prompt("Reason for rejection? (optional)", m.reject_reason || "");
      const col = m._source || "members";
      const ref = doc(db, col, m.id);

      await updateDoc(ref, {
        status: "rejected",
        reject_reason: reason || "",
        rejected_at: new Date().toISOString(),
        member_portal_access: false,
        benefits_enabled: false,
        updated_at: serverTimestamp(),
      });

      setMembers((prev) =>
        prev.map((x) =>
          x.id === m.id && x._source === col
            ? {
                ...x,
                status: "rejected",
                reject_reason: reason || "",
                member_portal_access: false,
                benefits_enabled: false,
              }
            : x
        )
      );
    } catch (err) {
      console.error("Reject error:", err);
      alert("Error rejecting member: " + err.message);
    }
  };

  const handleDelete = async (m) => {
    const ok = window.confirm(
      `Are you sure you want to permanently delete member "${m.name}" (${m.member_id || "APPLIED"})?`
    );
    if (!ok) return;

    try {
      const col = m._source || "members";
      await deleteDoc(doc(db, col, m.id));
      setMembers((prev) => prev.filter((x) => !(x.id === m.id && x._source === col)));
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error deleting member: " + err.message);
    }
  };

  const openEdit = (m) => {
    setEditingMember({
      ...m,
      district: m.district || "",
      taluka: m.taluka || "",
      city: m.city || "",
      state: m.state || "",
      address: m.address || "",
      mobile: m.mobile || "",
      occupation: m.occupation || "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingMember((prev) => ({ ...prev, [name]: value }));
  };

  const saveEdit = async () => {
    if (!editingMember) return;
    setSavingEdit(true);
    try {
      const col = editingMember._source || "members";
      const ref = doc(db, col, editingMember.id);

      const payload = {
        name: editingMember.name || "",
        mobile: editingMember.mobile || "",
        address: editingMember.address || "",
        state: editingMember.state || "",
        district: editingMember.district || "",
        taluka: editingMember.taluka || "",
        city: editingMember.city || "",
        occupation: editingMember.occupation || "",
        updated_at: serverTimestamp(),
      };

      await updateDoc(ref, payload);

      setMembers((prev) =>
        prev.map((m) => (m.id === editingMember.id && m._source === col ? { ...m, ...payload } : m))
      );

      setEditingMember(null);
    } catch (err) {
      console.error("Edit save error:", err);
      alert("Error saving changes: " + err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // ------------ FILTER/REPORT HELPERS -------------
  const uniqueValues = (field) => {
    const set = new Set();
    members.forEach((m) => {
      const v = (m[field] || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };

  const stateOptions = uniqueValues("state");
  const districtOptions = uniqueValues("district");
  const talukaOptions = uniqueValues("taluka");
  const cityOptions = uniqueValues("city");

  const filtered = useMemo(() => {
    let list = [...members];

    if (statusFilter !== "all") list = list.filter((m) => (m.status || "pending") === statusFilter);
    if (typeFilter !== "all") list = list.filter((m) => m.membership_type === typeFilter);
    if (stateFilter !== "all") list = list.filter((m) => (m.state || "").trim() === stateFilter);
    if (districtFilter !== "all") list = list.filter((m) => (m.district || "").trim() === districtFilter);
    if (talukaFilter !== "all") list = list.filter((m) => (m.taluka || "").trim() === talukaFilter);
    if (cityFilter !== "all") list = list.filter((m) => (m.city || "").trim() === cityFilter);

    const needle = search.trim().toLowerCase();
    if (needle) {
      list = list.filter((m) => {
        const s = `${m.name || ""} ${m.mobile || ""} ${m.member_id || ""}`.toLowerCase();
        return s.includes(needle);
      });
    }

    list.sort((a, b) => {
      if (sortBy === "created_desc") {
        const aa = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bb - aa;
      }
      if (sortBy === "created_asc") {
        const aa = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return aa - bb;
      }
      if (sortBy === "name_asc") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "name_desc") return (b.name || "").localeCompare(a.name || "");
      return 0;
    });

    return list;
  }, [
    members,
    statusFilter,
    typeFilter,
    stateFilter,
    districtFilter,
    talukaFilter,
    cityFilter,
    search,
    sortBy,
  ]);

  const stats = useMemo(() => {
    const total = members.length;
    const approved = members.filter((m) => m.status === "approved").length;
    const pending = members.filter((m) => !m.status || m.status === "pending").length;
    const rejected = members.filter((m) => m.status === "rejected").length;
    const regular = members.filter((m) => m.membership_type === "REGULAR").length;
    const nominal = members.filter((m) => m.membership_type === "NOMINAL").length;
    return { total, approved, pending, rejected, regular, nominal };
  }, [members]);

  const downloadCsv = () => {
    if (!filtered.length) {
      alert("No members in current filter to export.");
      return;
    }
    const header = [
      "Name",
      "Type",
      "Status",
      "Membership ID",
      "Mobile",
      "State",
      "District",
      "Taluka",
      "City",
      "Address",
      "Occupation",
      "Applied On",
      "Payment Status",
      "Manual UTR",
    ];
    const rows = filtered.map((m) =>
      [
        m.name || "",
        MEMBER_TYPE_LABEL[m.membership_type] || "Nominal",
        m.status || "pending",
        m.member_id || "APPLIED",
        m.mobile || "",
        m.state || "",
        m.district || "",
        m.taluka || "",
        m.city || "",
        (m.address || "").replace(/\r?\n/g, " "),
        m.occupation || "",
        m.created_at || "",
        m.payment_status || "",
        m.payment_manual_utr || "",
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`)
    );

    const csv = [header.map((h) => `"${h}"`).join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "members_report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // summary cards
  const summary = stats;

  // ---------------- PAGE CONTENT BLOCK ----------------
  const PageContent = (
    <div className="w-full h-full flex flex-col">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <StatCard label="Total" value={summary.total} tone="emerald" />
        <StatCard label="Approved" value={summary.approved} tone="sky" />
        <StatCard label="Pending" value={summary.pending} tone="amber" />
        <StatCard label="Rejected" value={summary.rejected} tone="rose" />
        <StatCard label="Regular" value={summary.regular} tone="indigo" />
        <StatCard label="Nominal" value={summary.nominal} tone="lime" />
      </div>

      {/* Filters + controls */}
      <div className="mb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-slate-900">Members Management</h2>
          <p className="text-xs md:text-sm text-slate-500 mt-0.5">
            View all membership applications (Regular & Nominal), approve / reject / edit, and manage membership numbers.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center justify-end">
          {!fullScreen ? (
            <button
              type="button"
              onClick={() => setFullScreen(true)}
              className="text-xs md:text-sm px-3 py-1 rounded bg-slate-900 text-white hover:bg-slate-950 border border-black"
            >
              ⛶ Open Full Screen
            </button>
          ) : null}

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white">
            <option value="all">Status: All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white">
            <option value="all">Type: All</option>
            <option value="REGULAR">Regular</option>
            <option value="NOMINAL">Nominal</option>
          </select>

          <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white">
            <option value="all">State: All</option>
            {stateOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white">
            <option value="all">District: All</option>
            {districtOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select value={talukaFilter} onChange={(e) => setTalukaFilter(e.target.value)} className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white">
            <option value="all">Taluka: All</option>
            {talukaOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white">
            <option value="all">City/Village: All</option>
            {cityOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white">
            <option value="created_desc">Newest first</option>
            <option value="created_asc">Oldest first</option>
            <option value="name_asc">Name A–Z</option>
            <option value="name_desc">Name Z–A</option>
          </select>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / mobile / ID"
            className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white min-w-[180px]"
          />

          <button type="button" onClick={downloadCsv} className="text-xs md:text-sm px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">
            ⬇ Download CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
          ⚠ Firestore error while loading <b>members</b>: <span className="font-mono">{error}</span>
        </div>
      )}

      {/* Main table */}
      <div className="flex-1 min-h-0 bg-white border border-black rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-4 text-sm text-slate-500">Loading members…</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">No members found for selected filters.</div>
        ) : (
          <div className="overflow-auto h-full">
            <table className="min-w-full text-xs md:text-sm border-collapse border border-black">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <Th align="center" styleClass="w-10">#</Th>
                  <Th>Member Name / Address</Th>
                  <Th align="center">Type</Th>
                  <Th align="center">Status</Th>
                  <Th align="center">Payment</Th>
                  <Th align="center">Benefits</Th>
                  <Th>State / District / Taluka / City</Th>
                  <Th>Mobile</Th>
                  <Th>Membership ID</Th>
                  <Th align="right">Fee / Total (₹)</Th>
                  <Th>Applied On</Th>
                  <Th align="center">Actions</Th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((m, idx) => {
                  const pay = String(m.payment_status || "").toUpperCase();
                  const paid = pay === "PAID" || m.isPaid === true;

                  return (
                    <tr key={`${m._source}-${m.id}`} className="hover:bg-slate-50">
                      <Td align="center">{idx + 1}</Td>

                      <Td>
                        <div className="font-semibold text-slate-900">{m.name || "-"}</div>
                        <div className="text-[11px] text-slate-600">
                          {(m.address || "").length > 90 ? (m.address || "").slice(0, 90) + "…" : (m.address || "")}
                        </div>
                      </Td>

                      <Td align="center">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-100 text-slate-700">
                          {MEMBER_TYPE_LABEL[m.membership_type] || "Nominal"}
                        </span>
                      </Td>

                      <Td align="center">
                        <StatusPill status={m.status || "pending"} />
                      </Td>

                      <Td align="center">
                        <span className={cx(
                          "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border",
                          paid ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"
                        )}>
                          {paid ? "PAID" : "PENDING"}
                        </span>
                      </Td>

                      <Td align="center">
                        <BenefitsPill
                          enabled={
                            m.status === "approved" &&
                            (m.benefits_enabled !== false || m.member_portal_access === true)
                          }
                        />
                      </Td>

                      <Td>
                        <div className="text-[11px] text-slate-800">
                          {m.state || "-"}
                          {m.district ? ` / ${m.district}` : ""}
                          {m.taluka ? ` / ${m.taluka}` : ""}
                          {m.city ? ` / ${m.city}` : ""}
                        </div>
                      </Td>

                      <Td>{m.mobile || "-"}</Td>

                      <Td className="font-mono text-[11px]">{m.member_id || "APPLIED"}</Td>

                      <Td align="right">{m.membership_fee || m.total || "-"}</Td>

                      <Td className="text-[11px]">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : "-"}
                      </Td>

                      <Td align="center">
                        <div className="flex justify-center gap-1 flex-wrap">
                          <button
                            onClick={() => openEdit(m)}
                            className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-[11px] border border-black hover:bg-slate-200"
                          >
                            Edit
                          </button>

                          {/* ✅ NEW: Mark Paid button (manual proof) */}
                          {!paid && (
                            <button
                              onClick={async () => {
                                const ok = window.confirm("Mark this member as PAID (manual, based on proof)?");
                                if (!ok) return;
                                try {
                                  const utr = window.prompt("Enter UTR / Txn ID (optional):", "") || "";
                                  await markPaidManual(m, { utr });
                                  alert("✅ Marked as PAID.");
                                } catch (e) {
                                  alert("❌ Failed to mark PAID: " + (e?.message || e));
                                }
                              }}
                              className="px-2 py-1 rounded bg-indigo-600 text-white text-[11px] border border-black hover:bg-indigo-700"
                            >
                              Mark Paid
                            </button>
                          )}

                          {m.status !== "approved" && (
                            <button
                              onClick={() => handleApprove(m)}
                              className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] border border-black hover:bg-emerald-700"
                            >
                              Approve
                            </button>
                          )}

                          {m.status !== "rejected" && (
                            <button
                              onClick={() => handleReject(m)}
                              className="px-2 py-1 rounded bg-amber-500 text-white text-[11px] border border-black hover:bg-amber-600"
                            >
                              Reject
                            </button>
                          )}

                          <button
                            onClick={() => handleDelete(m)}
                            className="px-2 py-1 rounded bg-rose-600 text-white text-[11px] border border-black hover:bg-rose-700"
                          >
                            Delete
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingMember && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-300 w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Edit Member</div>
                <div className="text-sm font-bold text-slate-900">{editingMember.name || "-"}</div>
                <div className="text-[11px] text-slate-500 font-mono">
                  {editingMember.member_id || "APPLIED"} •{" "}
                  <span className="uppercase">{editingMember._source}</span>
                </div>
              </div>
              <button onClick={() => setEditingMember(null)} className="text-sm text-slate-500 hover:text-slate-800">
                ✕
              </button>
            </div>

            <div className="px-5 py-3 overflow-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
              <FieldInput label="Full Name" name="name" value={editingMember.name || ""} onChange={handleEditChange} />
              <FieldInput label="Mobile" name="mobile" value={editingMember.mobile || ""} onChange={handleEditChange} />
              <FieldInput label="State" name="state" value={editingMember.state || ""} onChange={handleEditChange} />
              <FieldInput label="District" name="district" value={editingMember.district || ""} onChange={handleEditChange} />
              <FieldInput label="Taluka" name="taluka" value={editingMember.taluka || ""} onChange={handleEditChange} />
              <FieldInput label="City / Village" name="city" value={editingMember.city || ""} onChange={handleEditChange} />
              <FieldInput label="Occupation" name="occupation" value={editingMember.occupation || ""} onChange={handleEditChange} />
              <FieldInput label="Address" name="address" as="textarea" value={editingMember.address || ""} onChange={handleEditChange} />
            </div>

            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingMember(null)}
                className="px-4 py-1.5 rounded-lg bg-slate-200 text-[11px] text-slate-700 hover:bg-slate-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={savingEdit}
                className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-60"
              >
                {savingEdit ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ✅ FULL SCREEN OVERLAY WRAPPER
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-[99998] bg-white">
        <div className="h-full w-full flex flex-col">
          <div className="px-4 py-3 bg-slate-950 text-white flex items-center justify-between border-b border-black">
            <div>
              <div className="text-[12px] font-extrabold">Members Management — Full Screen</div>
              <div className="text-[11px] text-white/80">This is the full-screen list window.</div>
            </div>
            <button
              onClick={() => setFullScreen(false)}
              className="px-3 py-1.5 rounded-lg border border-white/20 bg-white/10 hover:bg-white/15 text-[12px] font-extrabold"
            >
              ✕ Close Full Screen
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden p-4">{PageContent}</div>
        </div>
      </div>
    );
  }

  return <div className="w-full">{PageContent}</div>;
}

function Th({ children, align = "left", styleClass = "" }) {
  return (
    <th
      className={cx(
        "px-2 py-2 text-[11px] font-semibold text-slate-700 border border-black",
        align === "center" && "text-center",
        align === "right" && "text-right",
        styleClass
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left" }) {
  return (
    <td
      className={cx(
        "px-2 py-1.5 text-[11px] text-slate-800 border border-black align-top",
        align === "center" && "text-center",
        align === "right" && "text-right"
      )}
    >
      {children}
    </td>
  );
}

function StatusPill({ status }) {
  const s = status || "pending";
  const style =
    s === "approved"
      ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
      : s === "rejected"
      ? "bg-rose-100 text-rose-700 border border-rose-300"
      : "bg-amber-50 text-amber-700 border border-amber-300";
  const label = s === "approved" ? "Approved" : s === "rejected" ? "Rejected" : "Pending";
  return (
    <span className={cx("inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold", style)}>
      {label}
    </span>
  );
}

function BenefitsPill({ enabled }) {
  const style = enabled
    ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
    : "bg-slate-50 text-slate-500 border border-slate-300";
  const label = enabled ? "Enabled" : "Disabled";
  return (
    <span className={cx("inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold", style)}>
      {label}
    </span>
  );
}

function StatCard({ label, value, tone }) {
  const map = {
    emerald: "from-emerald-500 to-emerald-600",
    sky: "from-sky-500 to-sky-600",
    amber: "from-amber-500 to-amber-600",
    rose: "from-rose-500 to-rose-600",
    indigo: "from-indigo-500 to-indigo-600",
    lime: "from-lime-500 to-lime-600",
  };
  const gradient = map[tone] || map.emerald;
  return (
    <div className="bg-white border border-slate-300 rounded-xl shadow-sm overflow-hidden">
      <div className={cx("px-3 py-1.5 text-[10px] font-semibold text-white bg-gradient-to-r", gradient)}>
        {label}
      </div>
      <div className="px-3 py-2">
        <div className="text-lg font-extrabold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function FieldInput({ label, name, value, onChange, as = "input" }) {
  const common =
    "w-full border border-slate-300 rounded-lg px-2 py-1.5 bg-white text-[11px] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500";
  return (
    <div>
      <div className="mb-0.5 text-[11px] font-semibold text-slate-700">{label}</div>
      {as === "textarea" ? (
        <textarea name={name} value={value} onChange={onChange} rows={3} className={common} />
      ) : (
        <input type="text" name={name} value={value} onChange={onChange} className={common} />
      )}
    </div>
  );
}

export default MembersPane;
